import React, { ElementType, ReactNode } from 'react';

import { textGray } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { useIntl } from 'react-intl';

import AnimatedPopup from './AnimatedPopup';

export interface PopupMenuItem {
  id: string | number;
  text?: string;
  textColor?: string;
  icon?: IconDefinition;
  iconColor?: string;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  component?: ReactNode;
  toggle?: boolean;
  checked?: boolean;
  disabled?: boolean;
  as?: ElementType | null | string;
  tabIndex?: number;
}

export type PopupMenuProps<T> = {
  isVisible: boolean;
  hide: () => void;
  liveAnnouncement?: string;
  children?: ReactNode;
  keyExtractor: (item: T) => string | number;
  renderControl?: boolean; // Don't render any parents, full render control is given to user.
  renderItem: (item: T, index: number) => ReactNode;
  items?: T[];
  heading?: string;
  showHeader?: boolean;
  centerHeading?: boolean;
  placeholder?: string;
  containerClasses?: string;
  containerStyle?: React.CSSProperties;
  footer?: ReactNode;
  hideOnScroll?: boolean;
  hideEvent?: 'scroll' | 'wheel';
  prevent?: boolean;
  isPortal?: boolean;
  align?: string;
  disableHide?: boolean;
  forceFixedPosition?: boolean;
  popupItemIsFocusable?: 0 | -1;
  displayCreatePlaylistBar?: boolean;
  createPlaylistBar?: ReactNode;
  customBoxStyle?: React.CSSProperties;
};

/**
 * A PopupMenu for creating dropdowns for settings or actions to click.
 */
const PopupMenu = <T extends PopupMenuItem>(props: PopupMenuProps<T>) => {
  const {
    isVisible,
    hide,
    children,
    items = [],
    heading,
    centerHeading,
    showHeader = true,
    renderControl = false,
    renderItem,
    keyExtractor,
    containerClasses = '',
    containerStyle = {},
    footer = null,
    hideOnScroll,
    hideEvent = 'scroll',
    prevent,
    isPortal,
    align,
    disableHide = false,
    forceFixedPosition = false,
    popupItemIsFocusable = -1,
    displayCreatePlaylistBar = false,
    createPlaylistBar = null,
    customBoxStyle,
    liveAnnouncement = '',
  } = props;

  const intl = useIntl();

  const handlePrevent = (e: any) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const ariaLabel = intl.formatMessage({
    id: 'popupMenu.close',
    defaultMessage: 'Close menu',
  });

  return (
    <AnimatedPopup
      isPortal={isPortal}
      hideOnScroll={hideOnScroll}
      isVisible={isVisible}
      hide={hide}
      align={align}
      disableHide={disableHide}
      hideEvent={hideEvent}
      forceFixedPosition={forceFixedPosition}
      render={() => (
        <Box
          className={`mt-2 max-h-[calc(100vh-2rem)] min-w-[200px] overflow-auto rounded-lg border border-gray-50 bg-white shadow-md ${containerClasses}`}
          data-testid="popup-container"
          onClick={(e) => e.stopPropagation()}
          style={containerStyle}
        >
          {showHeader && (
            <Flex
              className={`p-${
                heading ? '2.5' : '2.5 2.5 0 2.5'
              } items-center justify-center`}
            >
              <P
                className={`flex-grow opacity-75 text-${
                  centerHeading ? 'center' : 'left'
                }`}
              >
                {heading}
              </P>
              <IconButton
                data-testid="popup-close-button"
                variant="transparent"
                className={`text-gray-700 ${textGray} text-sm`}
                icon={<Icon icon={faTimes} />}
                onClick={
                  prevent
                    ? (e: React.MouseEvent<HTMLButtonElement>) =>
                        handlePrevent(e)
                    : hide
                }
                ariaLabel={ariaLabel}
              />
            </Flex>
          )}

          {heading && <Box className="h-px w-full bg-gray-200" />}
          {displayCreatePlaylistBar && createPlaylistBar}
          <P aria-live="polite" className="sr-only">
            {liveAnnouncement}
          </P>
          <Box
            className={`p-3 pt-${heading ? '3' : '1'} pb-1`}
            style={{ maxHeight: '300px', overflowY: 'auto', ...customBoxStyle }}
          >
            {items.map((item, i) => {
              if (renderControl) return item.component || renderItem(item, i);

              return (
                <Box
                  tabIndex={popupItemIsFocusable}
                  data-testid="popup-menu.item"
                  onClick={item.onClick}
                  key={keyExtractor(item)}
                >
                  {item.component || renderItem(item, i)}
                </Box>
              );
            })}
          </Box>
          {footer}
        </Box>
      )}
    >
      {children != null && children}
    </AnimatedPopup>
  );
};

export default PopupMenu;
